<template>
  <QPageContainer>
    <QPage class="recruitment-candidates-list">
      <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
        <QPullToRefresh no-mouse @refresh="refreshRecruitment">
          <transition-group tag="div" name="candidate-list">
            <PermanentCandidatesCard
              v-for="candidate in candidates"
              :id="candidate.getId()"
              :key="candidate.getId()"
              :candidate="candidate"
              :recruitment="recruitment"
              :can-ignore="false"
              :can-contact="true"
              @on-contact-candidate="onContactCandidate"
              @go-to-profile="goToProfile"
            />
          </transition-group>

          <NoCandidate v-if="candidates.length === 0" :img="chosenCandidateImg" :title="$t('ETAB.PAGES.CANDIDATE.@NO_IGNORED_CANDIDATES')" />
        </QPullToRefresh>
      </transition>
    </QPage>
    <QPageSticky v-if="$q.screen.gt.xs" position="bottom-right" :offset="[10, 8]">
      <QBtn fab icon="icon-f-rotate-cw" color="secondary" @click="refreshRecruitment" />
    </QPageSticky>
  </QPageContainer>
</template>

<script setup>
  import chosenCandidateImg from "@etab/assets/img/placeholders/candidate_chosen_placeholder.png";

  import {
    GETTER_RECRUITMENT_GET_CURRENT,
    ACTION_RECRUITMENT_CANDIDATES_GET_IGNORED,
    GETTER_RECRUITMENT_CANDIDATES_GET_IGNORED,
    ACTION_RECRUITMENT_GET,
  } from "@etab/stores/types";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useRecruitmentCandidatesStore } from "@etab/stores/recruitment-candidates";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { RecruitmentCandidatesService } from "@core/services";
  import { computed, onMounted } from "vue";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { useRoute } from "vue-router";
  import { useRecruitmentCandidate } from "@etab/composables";
  import { useTransition } from "@core/composables";

  import PermanentCandidatesCard from "@etab/components/candidates/PermanentCandidates.card.vue";
  import NoCandidate from "@etab/components/candidates/NoCandidate.functional.vue";

  const { transition } = useTransition();
  const { $_goToProfileFromPermanentDemand, $_scrollToPreviouslyOpenedExtra, $_onContactCandidate } = useRecruitmentCandidate();
  const route = useRoute();

  const recruitmentStore = useRecruitmentStore();
  const recruitmentCandidatesStore = useRecruitmentCandidatesStore();

  const demandId = computed(() => {
    return route.params.demandId;
  });

  const demandType = computed(() => {
    return route.params.demandType;
  });

  const recruitment = computed(() => {
    return recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
  });

  const candidates = computed(() => {
    return recruitmentCandidatesStore[GETTER_RECRUITMENT_CANDIDATES_GET_IGNORED];
  });

  const onContactCandidate = async function ({ candidate }) {
    $_onContactCandidate({ candidate, contactCallback: contactCandidate });
  };

  const contactCandidate = async function ({ candidate }) {
    try {
      LoadingManager.show();
      const candidateId = candidate.getId();
      await RecruitmentCandidatesService.contactPermanentCandidate({ demandId: demandId.value, candidateId });
      // add timeout because the message is long
      NotificationsManager.success({ message: "SUCCESS.@CANDIDATE_CONTACTED_SUCCESS", options: { timeout: 5000 } });
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      refreshRecruitment();
    }
  };

  const goToProfile = function ({ candidate }) {
    $_goToProfileFromPermanentDemand({ candidate });
  };

  const refreshRecruitment = async function (refresherDone) {
    const isFromPullToRefresh = typeof refresherDone === "function";
    try {
      if (!isFromPullToRefresh) {
        LoadingManager.show();
      }
      LoadingManager.show();
      await Promise.all([
        recruitmentStore[ACTION_RECRUITMENT_GET]({ demandId: demandId.value, demandType: demandType.value }),
        recruitmentCandidatesStore[ACTION_RECRUITMENT_CANDIDATES_GET_IGNORED]({ demandId: demandId.value, type: demandType.value }),
      ]);
    } catch (error) {
      NotificationsManager.error();
      throw error;
    } finally {
      LoadingManager.hide();
      if (isFromPullToRefresh) {
        refresherDone();
      }
    }
  };

  onMounted(() => {
    // stop loading started in route
    LoadingManager.hide();
    $_scrollToPreviouslyOpenedExtra();
  });
</script>
